import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelRoom } from 'app/typings/core/extranet/hotel/rooms';
import type { ExperienceCategory } from 'app/typings/experiences';

import type { PackDetails } from '../../../../utils/packs';
import { PackType } from '../../../../utils/packs';

type ScoreTagProps = {
  color: 'default' | 'red' | 'gold' | 'green';
  label: number | string;
};

const SCORE_THRESHOLDS: { max: number; color: ScoreTagProps['color'] }[] = [
  { max: 49, color: 'red' },
  { max: 59, color: 'gold' },
  { max: 100, color: 'green' },
];

export const getScoreTagProps = (score?: number): ScoreTagProps => {
  if (
    score === undefined ||
    !Number.isFinite(score) ||
    score < 0 ||
    score > 100
  ) {
    return { color: 'default', label: '-' };
  }

  const formatScore = Math.round(score);
  const threshold = SCORE_THRESHOLDS.find(({ max }) => formatScore <= max);

  return {
    color: threshold?.color ?? 'green',
    label: formatScore,
  };
};

const formatExperiencesTooltip = (
  experiences: PackDetails['availableExperiences'],
  currencyFormatter: (value: number) => string,
  experiencesList: ExtranetHotelExperience[],
  experiencesCategories?: ExperienceCategory[]
): string => {
  return experiences
    .sort((a, b) => b.discountedPrice - a.discountedPrice)
    .map((exp) => {
      const experience = experiencesList.find((e) => e.id === exp.experienceId);
      const experienceName =
        experiencesCategories?.find((cat) => cat.id === experience?.categoryId)
          ?.name ?? 'Unknown';
      const experiencePrice = currencyFormatter(exp.discountedPrice);

      return `${experienceName} (${experiencePrice})`;
    })
    .join(' + ');
};

const formatRoomTooltip = (
  room: ExtranetHotelRoom | undefined,
  price: number,
  currencyFormatter: (value: number) => string
): string | null => {
  if (!room) {
    return null;
  }

  const roomName = room.name ?? room.categoryName ?? 'Unknown';

  const roomPrice = currencyFormatter(price);

  return `${roomName} (${roomPrice})`;
};

export const formatTooltip = (
  values: PackDetails,
  currencyFormatter: (value: number) => string,
  roomsList: ExtranetHotelRoom[],
  experiencesList: ExtranetHotelExperience[],
  experiencesCategories?: ExperienceCategory[],
  roomId?: number
): string | null => {
  if (!values) {
    return null;
  }

  const tooltipExperiences = formatExperiencesTooltip(
    values.availableExperiences,
    currencyFormatter,
    experiencesList,
    experiencesCategories
  );

  if (values.type === PackType.WITHOUT_ROOM) {
    return tooltipExperiences || null;
  }

  let targetRoomId = values.lowestRoomId;
  let roomPrice = values.lowestRoomPrice ?? 0;

  if (roomId !== undefined) {
    const roomDetails = values.roomsDetails.find((r) => r.roomId === roomId);

    if (roomDetails?.discountedTotalPrice === undefined) {
      return null;
    }

    const experiencesDiscountedTotal = values.availableExperiences.reduce(
      (total, exp) => total + exp.discountedPrice,
      0
    );

    targetRoomId = roomId;
    roomPrice = roomDetails.discountedTotalPrice - experiencesDiscountedTotal;
  }

  const room = roomsList.find((r) => r.id === targetRoomId);
  const tooltipRoom = formatRoomTooltip(room, roomPrice, currencyFormatter);

  if (!tooltipRoom) {
    return null;
  }

  if (!tooltipExperiences) {
    return tooltipRoom;
  }

  return `${tooltipRoom} + ${tooltipExperiences}`;
};
